/**
 * ModuleUnavailableRedirect
 *
 * Renders a `<Navigate>` to `/module-unavailable?return=<currentPath>` so a
 * route-level commercial-module guard's denial surfaces as the editorial
 * "module not enabled" page (`ModuleUnavailablePage`) instead of the RBAC
 * 403 (`AccessDeniedRedirect`) — the block is "module off for this
 * location", not "missing permission".
 *
 * Used by the module-aware route guards in `App.tsx` (`ModulePageGuard`,
 * `TreasuryPageGuard`, `ModuleProtectedPage`). Encodes path + search + hash
 * so a deep-link round-trips cleanly.
 */

import { Navigate, useLocation } from 'react-router-dom';

export interface ModuleUnavailableRedirectProps {
  /**
   * The CommercialModule key that is switched off (e.g. `treasury`).
   * Surfaced on the editorial page as a friendly module label.
   */
  module?: string;
  /** Optional location label — pairs with `module` in the diagnostic block. */
  scope?: string;
}

export function ModuleUnavailableRedirect({
  module,
  scope,
}: ModuleUnavailableRedirectProps = {}) {
  const location = useLocation();
  const params = new URLSearchParams({
    return: `${location.pathname}${location.search}${location.hash}`,
  });
  if (module) params.set('module', module);
  if (scope) params.set('scope', scope);
  return <Navigate to={`/module-unavailable?${params.toString()}`} replace />;
}
